import { useCallback, useState } from 'react'
import { Trans } from 'react-i18next'

import { AppBreakpoint } from '@makyoapp/makyo-sdk/models/AppBuilder'
import { Moon24, Sun24 } from '@makyoapp/makyo-icon/icon'
import { Field, Switch } from '@makyoapp/makyo-ui'

import { NavbarSettings } from '@/adapters/__project__/BuilderNavbarAdapter'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useBuilderColorSchemeV2 } from '@/hooks/__project__/useBuilderColorSchemeV2'
import { useActiveDevice } from '@/hooks/useActiveDevice'
import { BUILDER_DEVICES } from '@/shared/builder-constants'
import { SORTED_NAV_SETTINGS_KEY } from '@/shared/main-navigation-bar'
import { debounce } from '@/shared/utils'
import { Accordion, AccordionItem } from '@/components/Accordion/Accordion'
import { BrandKitColorsDropdown } from '@/components/BrandKitColorsDropdown'

import { NavbarItemSettings } from './NavbarItemSettings'

interface ApplyToAllProps {
  viewport: string
  label: string
  value: any
  parent?: string
}

/*
 * NavigationBarAccordionItem component is a functional component.
 * It renders an AccordionItem with a few fields containing switches to toggle certain navigation bar settings.
 */
function NavigationBarAccordionItem() {
  const [applyToAll, setApplyToAll] = useState<ApplyToAllProps[]>([])
  const [isApplying, setIsApplying] = useState(false)

  const [activeDevice, setActiveDevice] = useActiveDevice()
  const { navbarSettings, setNavbarSettings, updateAllNavbarSettings } = useAppBuilderContext()

  const [scheme] = useBuilderColorSchemeV2()
  const mode = scheme.current

  const activeNavbar = navbarSettings[activeDevice]

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedUpdateNavbarSettings = useCallback(
    debounce((updatedSettings) => {
      updateAllNavbarSettings(updatedSettings)
    }, 1500),
    []
  )

  const displayOptionList = ['show_shadow', 'sticky', 'show_icon', 'show_label', 'show_hover', 'show_active_indicator']

  const handleSwitchChange = useCallback(
    (viewport: AppBreakpoint, name: string, checked: boolean, _setting?: string, action?: string) => {
      const updatedSettings = {
        ...navbarSettings[viewport],
        displayOptions: navbarSettings[viewport].displayOptions.map((option) =>
          option.name === name || option.checked === undefined ? { ...option, checked } : option
        ),
      }
      if (action && action === 'removeLogo') {
        updatedSettings.logo = { value: '', label: '' }
      }

      const newNavbarSettings = { ...navbarSettings, [viewport]: updatedSettings }

      setNavbarSettings(newNavbarSettings)

      const existingChange = applyToAll.find((item) => item.label === name && item.viewport === viewport)
      if (existingChange) {
        setApplyToAll((prevApplyToAll) =>
          prevApplyToAll.map((item) =>
            item.label === name && item.viewport === viewport ? { ...item, value: checked } : item
          )
        )
      } else {
        setApplyToAll((prevApplyToAll) => [
          ...prevApplyToAll,
          {
            label: name,
            value: checked,
            viewport,
            parent: displayOptionList.includes(name) ? 'displayOptions' : undefined,
          },
        ])
      }
      debouncedUpdateNavbarSettings(newNavbarSettings)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navbarSettings]
  )

  const handleFieldChange = useCallback(
    (viewport: AppBreakpoint, field: string, value: string) => {
      const isLogo = field === 'logo'

      let displayOptions = [...navbarSettings[viewport].displayOptions]
      const hasStickyOption = displayOptions.some((option) => option.name === 'sticky')

      if (!hasStickyOption) {
        displayOptions.push({
          label: 'Sticky',
          name: 'sticky',
          checked: true,
        })
      }

      displayOptions = displayOptions.map((option) =>
        option.name === 'show_logo' || option.checked === undefined
          ? { ...option, checked: isLogo || option.checked }
          : option
      )

      const updatedSettings = {
        ...navbarSettings[viewport],
        [field]: value,
        displayOptions,
      }

      const newNavbarSettings = { ...navbarSettings, [viewport]: updatedSettings }
      setNavbarSettings(newNavbarSettings)

      // update apply to all
      const existingChange = applyToAll.find((item) => item.label === field && item.viewport === viewport)
      if (existingChange) {
        setApplyToAll((prevApplyToAll) =>
          prevApplyToAll.map((item) => (item.label === field && item.viewport === viewport ? { ...item, value } : item))
        )
      } else {
        setApplyToAll((prevApplyToAll) => [
          ...prevApplyToAll,
          { label: field, value, viewport, parent: displayOptionList.includes(field) ? 'displayOptions' : undefined },
        ])
      }

      debouncedUpdateNavbarSettings(newNavbarSettings)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navbarSettings]
  )

  const handleApplyAllChanges = useCallback(() => {
    setIsApplying(true)
    const updatedSettings = (Object.keys(navbarSettings) as AppBreakpoint[]).reduce(
      (acc, device) => {
        acc[device] = { ...navbarSettings[device] }
        applyToAll.forEach(({ label, value, parent }) => {
          if (parent === 'displayOptions' && acc[device][parent]) {
            acc[device][parent] = acc[device][parent].map((option: any) =>
              option.name === label ? { ...option, checked: value } : option
            )
          } else if (!parent) {
            acc[device][label as keyof NavbarSettings] = value as never
            if (device === 'phone') acc[device].position = 'bottom'
          }
        })

        if (acc[device].displayOptions) {
          const hasStickyOption = acc[device].displayOptions.some((option: any) => option.name === 'sticky')

          if (!hasStickyOption) {
            acc[device].displayOptions.push({
              label: 'Sticky',
              name: 'sticky',
              checked: true,
            })
          }
        }

        return acc
      },
      {} as typeof navbarSettings
    )
    updateAllNavbarSettings(updatedSettings)
    setNavbarSettings(updatedSettings)
    setApplyToAll([])
    const timeout = setTimeout(() => {
      setIsApplying(false)
      return () => clearTimeout(timeout)
    }, 1500)
    return () => clearTimeout(timeout)
  }, [applyToAll, navbarSettings, setNavbarSettings, updateAllNavbarSettings])

  const handleUpdateNavbarSetting = useCallback(
    (field: string, value: any) => {
      const updatedSettings = (Object.keys(navbarSettings) as AppBreakpoint[]).reduce(
        (acc, device) => {
          acc[device] = { ...navbarSettings[device] }
          acc[device][field as keyof NavbarSettings] = value as never
          if (acc[device].displayOptions) {
            const hasStickyOption = acc[device].displayOptions.some((option: any) => option.name === 'sticky')

            if (!hasStickyOption) {
              acc[device].displayOptions.push({
                label: 'Sticky',
                name: 'sticky',
                checked: true,
              })
            }
          }

          if (device === 'phone') {
            acc[device].position = 'bottom'
          }

          return acc
        },
        {} as typeof navbarSettings
      )
      updateAllNavbarSettings(updatedSettings)
      setNavbarSettings(updatedSettings)
    },
    [navbarSettings, setNavbarSettings, updateAllNavbarSettings]
  )

  const ModeIcon = mode === 'dark' ? <Moon24 className='w-3.5 h-3.5' /> : <Sun24 className='w-3.5 h-3.5' />

  return (
    <AccordionItem itemId='setting-navigation-bar' title={<Trans>Navigation Bar</Trans>} righSection={ModeIcon}>
      <Field isVertical className='py-0 flex items-center! flex-row! justify-between! min-h-10!'>
        <label className='w-5/12'>
          <Trans>Show</Trans>
        </label>
        <Switch checked={activeNavbar?.show} onClick={(e) => handleUpdateNavbarSetting('show', e)} />
      </Field>

      {activeNavbar?.show && (
        <>
          <Field>
            <label className='w-4/12'>
              <Trans>Color {mode === 'dark' ? '(Dark)' : '(Light)'}</Trans>
            </label>
            <BrandKitColorsDropdown
              value={mode === 'dark' ? activeNavbar.menuBgColorDark : activeNavbar.menuBgColor}
              onChange={(value) => {
                handleUpdateNavbarSetting(mode === 'dark' ? 'menuBgColorDark' : 'menuBgColor', value)
              }}
              showExtendedColors
            />
          </Field>

          <div className='p-0 dark:bg-slate-600/90 border dark:border-slate-700/45 rounded-md'>
            <Accordion openMultiple={false} initialActiveItems={['setting-navigation-bar']}>
              {SORTED_NAV_SETTINGS_KEY.map((key) => {
                let { title, viewport, ...rest } = navbarSettings[key]

                const isActive = activeDevice === viewport

                const DeviceIcon = BUILDER_DEVICES[viewport].Icon

                const TitleAccordion = (
                  <>
                    {DeviceIcon && <DeviceIcon className='h-4 w-4 mx-2 inline-block' />}
                    {title}
                  </>
                )

                return (
                  <AccordionItem
                    key={viewport}
                    className={isActive ? 'dark:bg-slate-700/80' : ''}
                    title={TitleAccordion}
                    itemId={viewport}
                    onClick={() => setActiveDevice(viewport)}
                  >
                    <NavbarItemSettings
                      onFieldChange={(field, value) => handleFieldChange(viewport, field, value)}
                      onSwitchChange={(name, checked, setting, action) =>
                        handleSwitchChange(viewport, name, checked, setting, action)
                      }
                      settings={rest}
                      handleApplyAllChanges={handleApplyAllChanges}
                      isApplying={isApplying}
                      ModeIcon={ModeIcon}
                    />
                  </AccordionItem>
                )
              })}
            </Accordion>
          </div>
        </>
      )}
    </AccordionItem>
  )
}

export default NavigationBarAccordionItem
